import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Bus, GraduationCap, Users, MapPin, Calendar, Clock, Plus, Trash2, DollarSign, Search, TrendingUp, Receipt, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = {
  Planned: 'bg-blue-100 text-blue-700',
  Ongoing: 'bg-yellow-100 text-yellow-700',
  Completed: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
};

export default function TripDetailPage() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [buses, setBuses] = useState([]);
  const [tripStudents, setTripStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bus form
  const [busDialogOpen, setBusDialogOpen] = useState(false);
  const [busForm, setBusForm] = useState({ bus_name: '', driver_name: '', capacity: '' });

  // Helper: remaining seats per bus
  const getRemaining = (bus) => bus.capacity - tripStudents.filter(s => s.bus_id === bus.id).length;

  // Student assign form
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedBusForStudents, setSelectedBusForStudents] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [studentPayments, setStudentPayments] = useState({}); // { studentId: { amount, reference } }

  // Payment form
  const [payments, setPayments] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    student_id: '', fee_amount: '', amount_paid: '', payment_date: '', payment_method: '', payment_reference: '',
  });
  const [paymentSearch, setPaymentSearch] = useState('');

  // Teacher assignment
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedBusForTeachers, setSelectedBusForTeachers] = useState('');

  // Expenses (for profit calc)
  const [expenses, setExpenses] = useState([]);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ expense_type: '', amount: '', description: '', expense_date: '' });

  const fetchData = async () => {
    try {
      const [tripRes, busRes, tripStudentRes, allStudentRes, teacherRes, paymentRes, assignedTeacherRes, expenseRes] = await Promise.all([
        axios.get(`${API}/trips/${tripId}`),
        axios.get(`${API}/trips/${tripId}/buses`),
        axios.get(`${API}/trips/${tripId}/students`),
        axios.get(`${API}/students`),
        axios.get(`${API}/teachers`),
        axios.get(`${API}/trips/${tripId}/payments`),
        axios.get(`${API}/trips/${tripId}/teachers`),
        axios.get(`${API}/trips/${tripId}/expenses`),
      ]);
      setTrip(tripRes.data);
      setBuses(busRes.data);
      setTripStudents(tripStudentRes.data);
      setAllStudents(allStudentRes.data);
      setAllTeachers(teacherRes.data);
      setPayments(paymentRes.data);
      setAssignedTeachers(assignedTeacherRes.data);
      setExpenses(Array.isArray(expenseRes.data) ? expenseRes.data : expenseRes.data.expenses || []);
    } catch {
      toast.error('Failed to load trip details');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [tripId]);

  // --- Bus actions ---
  const handleAddBus = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/trips/${tripId}/buses`, busForm);
      toast.success('Bus added');
      setBusForm({ bus_name: '', driver_name: '', capacity: '' });
      setBusDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add bus');
    }
  };

  const handleRemoveBus = async (busId) => {
    if (!window.confirm('Remove this bus?')) return;
    try {
      await axios.delete(`${API}/trips/${tripId}/buses/${busId}`);
      toast.success('Bus removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove bus');
    }
  };

  // --- Student actions ---
  const assignedStudentIds = tripStudents.map(s => s.id);
  const unassignedStudents = allStudents.filter(s => !assignedStudentIds.includes(s.id));

  const selectedBusObj = buses.find(b => b.id === selectedBusForStudents);
  const remainingSeats = selectedBusObj ? getRemaining(selectedBusObj) : 0;

  const toggleStudent = (id) => {
    setSelectedStudents(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      // prevent selecting more students than remaining seats
      if (selectedBusObj && prev.length >= remainingSeats) {
        toast.error(`This bus only has ${remainingSeats} seat${remainingSeats !== 1 ? 's' : ''} left`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleAssignStudents = async () => {
    if (!selectedBusForStudents) return toast.error('Select a bus first');
    if (selectedStudents.length === 0) return toast.error('Select at least one student');
    try {
      await axios.post(`${API}/trips/${tripId}/students`, {
        studentIds: selectedStudents,
        busId: selectedBusForStudents,
        studentPayments,
      });
      toast.success('Students assigned');
      setSelectedStudents([]);
      setSelectedBusForStudents('');
      setStudentSearch('');
      setStudentPayments({});
      setStudentDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign students');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Remove this student from the trip?')) return;
    try {
      await axios.delete(`${API}/trips/${tripId}/students/${studentId}`);
      toast.success('Student removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove student');
    }
  };

  // --- Payment actions ---
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/trips/${tripId}/payments`, paymentForm);
      toast.success('Payment recorded');
      setPaymentForm({ student_id: '', fee_amount: '', amount_paid: '', payment_date: '', payment_method: '', payment_reference: '' });
      setPaymentDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    }
  };

  // --- Teacher actions ---
  const assignedTeacherIds = assignedTeachers.map(t => t.teacher_id);
  const unassignedTeachers = allTeachers.filter(t => {
    // Exclude teachers already assigned to the selected bus
    if (!selectedBusForTeachers) return !assignedTeacherIds.includes(t.id);
    return !assignedTeachers.some(at => at.teacher_id === t.id && at.bus_id === selectedBusForTeachers);
  });

  const toggleTeacher = (id) => {
    setSelectedTeachers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleAssignTeachers = async () => {
    if (!selectedBusForTeachers) return toast.error('Select a bus first');
    if (selectedTeachers.length === 0) return toast.error('Select at least one teacher');
    try {
      await axios.post(`${API}/trips/${tripId}/buses/${selectedBusForTeachers}/teachers`, {
        teacherIds: selectedTeachers,
      });
      toast.success('Teachers assigned');
      setSelectedTeachers([]);
      setSelectedBusForTeachers('');
      setTeacherDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign teachers');
    }
  };

  const handleRemoveTeacher = async (busId, teacherId) => {
    if (!window.confirm('Remove this teacher from the bus?')) return;
    try {
      await axios.delete(`${API}/trips/${tripId}/buses/${busId}/teachers/${teacherId}`);
      toast.success('Teacher removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove teacher');
    }
  };

  // --- Expense actions ---
  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/trips/${tripId}/expenses`, expenseForm);
      toast.success('Expense added');
      setExpenseForm({ expense_type: '', amount: '', description: '', expense_date: '' });
      setExpenseDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add expense');
    }
  };

  const handleRemoveExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/trips/${tripId}/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete expense');
    }
  };

  // --- Delete trip ---
  const handleDeleteTrip = async () => {
    if (!window.confirm('Are you sure you want to delete this trip? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/trips/${tripId}`);
      toast.success('Trip deleted');
      navigate('/trips');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete trip');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="text-center py-16 text-slate-500">
        <p>Trip not found</p>
        <Link to="/trips">
          <Button variant="outline" className="mt-4">Back to Trips</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Link to="/trips">
            <Button variant="outline" size="sm">
              <ArrowLeft size={16} className="mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900">{trip.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-1 text-slate-500 text-sm">
              <span className="flex items-center gap-1"><MapPin size={14} /> {trip.location}</span>
              <span className="flex items-center gap-1"><Calendar size={14} /> {trip.trip_date}</span>
              <span className="flex items-center gap-1"><Clock size={14} /> {trip.departure_time} – {trip.return_time}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className={`text-sm px-3 py-1 ${statusColor[trip.status]}`}>
            {trip.status}
          </Badge>
          <Button size="sm" variant="outline" onClick={() => navigate(`/trips/${tripId}/print`)}>
            <Printer size={14} className="mr-1" /> Print Report
          </Button>
          <Button size="sm" variant="outline" className="text-red-500 border-red-300 hover:bg-red-50 hover:text-red-700" onClick={handleDeleteTrip}>
            <Trash2 size={14} className="mr-1" /> Delete Trip
          </Button>
        </div>
      </div>

      {/* Trip Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Fee per Student</p>
            <p className="text-2xl font-bold text-slate-900">${Number(trip.fee_per_student).toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Total Students</p>
            <p className="text-2xl font-bold text-slate-900">{tripStudents.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Description</p>
            <p className="text-sm text-slate-700 mt-1">{trip.description || 'No description'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      {(() => {
        const boys = tripStudents.filter(s => s.gender === 'Male').length;
        const girls = tripStudents.filter(s => s.gender === 'Female').length;
        const totalCollected = payments.reduce((sum, p) => sum + Number(p.amount_paid || 0), 0);
        const totalFees = payments.reduce((sum, p) => sum + Number(p.fee_amount || 0), 0);
        const totalBalance = payments.reduce((sum, p) => sum + Number(p.balance || 0), 0);
        const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
        const profit = totalCollected - totalExpenses;
        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-slate-500">Boys</p>
                <p className="text-xl font-bold text-blue-600">{boys}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-slate-500">Girls</p>
                <p className="text-xl font-bold text-pink-600">{girls}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-slate-500">Collected</p>
                <p className="text-xl font-bold text-green-600">${totalCollected.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-slate-500">Balance Left</p>
                <p className="text-xl font-bold text-orange-600">${totalBalance.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-slate-500">Expenses</p>
                <p className="text-xl font-bold text-red-600">${totalExpenses.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp size={12} /> Profit</p>
                <p className={`text-xl font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>${profit.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* ===== BUSES ===== */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Bus size={20} className="text-pink-500" />
            Buses ({buses.length})
          </CardTitle>
          <Dialog open={busDialogOpen} onOpenChange={setBusDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white">
                <Plus size={16} className="mr-1" /> Add Bus
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle className="font-heading">Add Bus</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddBus} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input value={busForm.bus_name} onChange={e => setBusForm({ ...busForm, bus_name: e.target.value })} required placeholder="e.g. National Express" />
                </div>
                <div className="space-y-2">
                  <Label>Driver Name</Label>
                  <Input value={busForm.driver_name} onChange={e => setBusForm({ ...busForm, driver_name: e.target.value })} required placeholder="Driver full name" />
                </div>
                <div className="space-y-2">
                  <Label>Number of Students it can Accommodate</Label>
                  <Input type="number" value={busForm.capacity} onChange={e => setBusForm({ ...busForm, capacity: e.target.value })} required placeholder="e.g. 40" min="1" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setBusDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-pink-500 hover:bg-pink-600 text-white">Add Bus</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {buses.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">No buses added yet. Add a bus to start assigning students.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {buses.map(bus => {
                  const assigned = tripStudents.filter(s => s.bus_id === bus.id).length;
                  const full = assigned >= bus.capacity;
                  return (
                    <TableRow key={bus.id}>
                      <TableCell className="font-medium">{bus.bus_name}</TableCell>
                      <TableCell>{bus.driver_name}</TableCell>
                      <TableCell>
                        <span className={full ? 'text-red-600 font-semibold' : ''}>
                          {assigned}/{bus.capacity}
                        </span>
                        {full && <Badge variant="outline" className="ml-2 text-xs text-red-600 border-red-300">Full</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveBus(bus.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ===== STUDENTS ===== */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <GraduationCap size={20} className="text-pink-500" />
            Students ({tripStudents.length})
          </CardTitle>
          {buses.length > 0 && (
            <Dialog open={studentDialogOpen} onOpenChange={(open) => { setStudentDialogOpen(open); if (!open) { setSelectedStudents([]); setSelectedBusForStudents(''); setStudentSearch(''); setStudentPayments({}); } }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white">
                  <Plus size={16} className="mr-1" /> Assign Students
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Assign Students to Trip</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label>Select Bus</Label>
                    <Select value={selectedBusForStudents} onValueChange={(v) => { setSelectedBusForStudents(v); setSelectedStudents([]); setStudentPayments({}); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a bus..." />
                      </SelectTrigger>
                      <SelectContent>
                        {buses.map(bus => {
                          const left = getRemaining(bus);
                          return (
                            <SelectItem key={bus.id} value={bus.id} disabled={left <= 0}>
                              {bus.bus_name} — {bus.driver_name} ({left} seat{left !== 1 ? 's' : ''} left)
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Search Students</Label>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Search by name..."
                        value={studentSearch}
                        onChange={e => setStudentSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Select Students ({selectedStudents.length}{selectedBusObj ? `/${remainingSeats}` : ''} selected)</Label>
                    {unassignedStudents.length === 0 ? (
                      <p className="text-sm text-slate-500 py-2">All students are already assigned to this trip.</p>
                    ) : (
                      <div className="border rounded-md max-h-60 overflow-y-auto">
                        {unassignedStudents
                          .filter(s => {
                            if (!studentSearch.trim()) return true;
                            const q = studentSearch.toLowerCase();
                            return `${s.first_name} ${s.last_name}`.toLowerCase().includes(q);
                          })
                          .map(s => {
                            const isChecked = selectedStudents.includes(s.id);
                            return (
                              <div key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b last:border-0">
                                <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleStudent(s.id)}
                                    className="rounded border-slate-300 text-pink-500 focus:ring-pink-500 shrink-0"
                                  />
                                  <span className="text-sm truncate">{s.first_name} {s.last_name}</span>
                                </label>
                                {isChecked && (
                                  <div className="flex items-center gap-1 shrink-0">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="Paid"
                                      className="w-20 h-8 text-xs"
                                      value={studentPayments[s.id]?.amount || ''}
                                      onChange={e => setStudentPayments(prev => ({ ...prev, [s.id]: { ...prev[s.id], amount: e.target.value } }))}
                                    />
                                    <Input
                                      placeholder="Receipt #"
                                      className="w-24 h-8 text-xs"
                                      value={studentPayments[s.id]?.reference || ''}
                                      onChange={e => setStudentPayments(prev => ({ ...prev, [s.id]: { ...prev[s.id], reference: e.target.value } }))}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  {trip && <p className="text-xs text-slate-500">Fee per student: ${Number(trip.fee_per_student).toFixed(2)} — enter amount paid and receipt number per student</p>}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setStudentDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={handleAssignStudents} disabled={selectedStudents.length === 0 || !selectedBusForStudents}>
                      Assign {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {tripStudents.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">
              {buses.length === 0 ? 'Add a bus first, then assign students.' : 'No students assigned yet. Click "Assign Students" to add them.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Bus</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tripStudents.map(student => {
                  const bus = buses.find(b => b.id === student.bus_id);
                  const paidStatusBadge = student.payment_status === 'Fully Paid'
                    ? 'bg-green-100 text-green-700'
                    : student.payment_status === 'Partially Paid'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700';
                  return (
                    <TableRow key={student.assignment_id || student.id}>
                      <TableCell className="font-medium">{student.full_name}</TableCell>
                      <TableCell className="text-xs">{student.gender || '—'}</TableCell>
                      <TableCell className="text-xs">{student.class_name || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{bus?.bus_name || '—'}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">${Number(student.paid || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs">${Number(student.balance || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        {student.payment_status && <Badge variant="secondary" className={`text-xs ${paidStatusBadge}`}>{student.payment_status}</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveStudent(student.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ===== TEACHERS ===== */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users size={20} className="text-pink-500" />
            Teachers ({assignedTeachers.length})
          </CardTitle>
          {buses.length > 0 && (
            <Dialog open={teacherDialogOpen} onOpenChange={(open) => { setTeacherDialogOpen(open); if (!open) { setSelectedTeachers([]); setSelectedBusForTeachers(''); } }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white">
                  <Plus size={16} className="mr-1" /> Assign Teachers
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Assign Teachers to Bus</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label>Select Bus</Label>
                    <Select value={selectedBusForTeachers} onValueChange={(v) => { setSelectedBusForTeachers(v); setSelectedTeachers([]); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a bus..." />
                      </SelectTrigger>
                      <SelectContent>
                        {buses.map(bus => (
                          <SelectItem key={bus.id} value={bus.id}>
                            {bus.bus_name} — {bus.driver_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Select Teachers ({selectedTeachers.length} selected)</Label>
                    {unassignedTeachers.length === 0 ? (
                      <p className="text-sm text-slate-500 py-2">All teachers are already assigned to this bus.</p>
                    ) : (
                      <div className="border rounded-md max-h-60 overflow-y-auto">
                        {unassignedTeachers.map(t => (
                          <label key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-0">
                            <input
                              type="checkbox"
                              checked={selectedTeachers.includes(t.id)}
                              onChange={() => toggleTeacher(t.id)}
                              className="rounded border-slate-300 text-pink-500 focus:ring-pink-500"
                            />
                            <span className="text-sm">{t.full_name || `${t.first_name} ${t.last_name}`}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setTeacherDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-pink-500 hover:bg-pink-600 text-white" onClick={handleAssignTeachers} disabled={selectedTeachers.length === 0 || !selectedBusForTeachers}>
                      Assign {selectedTeachers.length} Teacher{selectedTeachers.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {assignedTeachers.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">
              {buses.length === 0 ? 'Add a bus first, then assign teachers.' : 'No teachers assigned yet. Click "Assign Teachers" to add them.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Bus</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignedTeachers.map(teacher => (
                  <TableRow key={teacher.assignment_id}>
                    <TableCell className="font-medium">{teacher.full_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{teacher.bus_name}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveTeacher(teacher.bus_id, teacher.teacher_id)}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ===== EXPENSES ===== */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Receipt size={20} className="text-pink-500" />
            Expenses ({expenses.length})
          </CardTitle>
          <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white">
                <Plus size={16} className="mr-1" /> Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle className="font-heading">Add Expense</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddExpense} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={expenseForm.expense_type} onValueChange={(v) => setExpenseForm({ ...expenseForm, expense_type: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Transport">Transport</SelectItem>
                      <SelectItem value="Food">Food</SelectItem>
                      <SelectItem value="Entry Fees">Entry Fees</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount ($)</Label>
                    <Input type="number" step="0.01" min="0" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} required placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm({ ...expenseForm, expense_date: e.target.value })} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={expenseForm.description} onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })} placeholder="e.g. Bus fuel cost" />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-pink-500 hover:bg-pink-600 text-white">Add Expense</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">No expenses recorded yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map(exp => (
                  <TableRow key={exp.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{exp.expense_type}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">${Number(exp.amount).toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{exp.description || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-600">{exp.expense_date}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveExpense(exp.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ===== PAYMENTS ===== */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <DollarSign size={20} className="text-pink-500" />
            Payments ({payments.length})
          </CardTitle>
          {tripStudents.length > 0 && (
            <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white">
                  <Plus size={16} className="mr-1" /> Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="font-heading">Record Payment</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRecordPayment} className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label>Student</Label>
                    <Select value={paymentForm.student_id} onValueChange={(v) => setPaymentForm({ ...paymentForm, student_id: v, fee_amount: trip.fee_per_student || '' })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a student..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tripStudents.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fee Amount</Label>
                      <Input type="number" step="0.01" min="0" value={paymentForm.fee_amount} onChange={e => setPaymentForm({ ...paymentForm, fee_amount: e.target.value })} required placeholder="0.00" />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount Paid</Label>
                      <Input type="number" step="0.01" min="0" value={paymentForm.amount_paid} onChange={e => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })} required placeholder="0.00" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Date</Label>
                      <Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Method..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                          <SelectItem value="Card">Card</SelectItem>
                          <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Receipt / Reference Number</Label>
                    <Input value={paymentForm.payment_reference} onChange={e => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })} required placeholder="e.g. REC-00123" />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-pink-500 hover:bg-pink-600 text-white">Record Payment</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">
              {tripStudents.length === 0 ? 'Assign students first, then record payments.' : 'No payments recorded yet.'}
            </p>
          ) : (
            <div className="space-y-3">
              <div className="relative max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search payments by student name..."
                  value={paymentSearch}
                  onChange={e => setPaymentSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments
                    .filter(p => {
                      if (!paymentSearch.trim()) return true;
                      const student = tripStudents.find(s => s.id === p.student_id) || allStudents.find(s => s.id === p.student_id);
                      const name = student?.full_name || `${student?.first_name || ''} ${student?.last_name || ''}`;
                      return name.toLowerCase().includes(paymentSearch.toLowerCase());
                    })
                    .map(p => {
                      const student = tripStudents.find(s => s.id === p.student_id) || allStudents.find(s => s.id === p.student_id);
                      const statusBadge = p.payment_status === 'Fully Paid'
                        ? 'bg-green-100 text-green-700'
                        : p.payment_status === 'Partially Paid'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700';
                      return (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{student?.full_name || `${student?.first_name || ''} ${student?.last_name || ''}` || '—'}</TableCell>
                          <TableCell>${Number(p.fee_amount).toFixed(2)}</TableCell>
                          <TableCell>${Number(p.amount_paid).toFixed(2)}</TableCell>
                          <TableCell>${Number(p.balance).toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={`text-xs ${statusBadge}`}>{p.payment_status}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">{p.payment_reference}</TableCell>
                          <TableCell className="text-xs text-slate-600">{p.payment_date}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
